import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { PermissionKeys, useGetBuildingsByIdQuery } from '@client/api';
import { isDefinedString, PageDetailHeader, PageNavigation, Text } from '@client/toolkit';
import { useBuildingPackages } from '../../../../hooks/useBuildingPackages';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';

import { SpaceContext } from '../../context/space-context';
import { PortfolioPaths } from '../../portfolio.router';
import { SpacePermissionBasedCta } from '../../components/space-permission-based-cta/space-permission-based-cta';
import { PageContentAnimation } from '../../../../components/layout/page-content-animation';
// BuildingEditModal kept for potential future reversion to popup mode
// import { BuildingEditModal } from './components/building-edit-modal.component';
import { BuildingDeleteModal } from './components/building-delete-modal.component';

export function BuildingRoute() {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tEos } = useTranslation('eos');

  const { buildingId } = useParams<{ buildingId: string }>();

  const { data: building, isFetching } = useGetBuildingsByIdQuery({ id: buildingId ?? '' }, { skip: !buildingId });
  const { hasAnyPermission: hasSpacePermission, isLoading: isLoadingSpacePermissions } = useSpacePermissions({
    id: buildingId,
  });
  const hasUpdatePermission = isLoadingSpacePermissions || hasSpacePermission([PermissionKeys.Space.Update]);
  // Owner/Manager/Creator/Investor are exactly the roles PermissionKeys.Space.FinancialView is
  // granted to (see PermissionRegistry.SpaceFinancialViewRoles), so checking this permission also
  // covers inherited access correctly - the roles array reports "Inherited" for those, not the
  // underlying role, so checking roles directly would incorrectly hide these tabs.
  const hasFinancialViewPermission =
    isLoadingSpacePermissions || hasSpacePermission([PermissionKeys.Space.FinancialView]);
  const hasEnergyPermission = hasUpdatePermission;
  const { hasManagement, hasPortfolio, hasEos, isPortfolioOnly } = useBuildingPackages(building?.packages);

  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<boolean>(false);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        key: 'dashboard',
        label: tGlobal('summary'),
        to: generatePath(PortfolioPaths.Building, { buildingId }),
        end: true,
      },
      ...(hasUpdatePermission
        ? [
            {
              key: 'arealer',
              label: tGlobal('entity.area', { context: 'plural' }),
              to: generatePath(PortfolioPaths.BuildingArealer, { buildingId }),
              end: false,
            },
          ]
        : []),
      ...(hasManagement && hasFinancialViewPermission
        ? [
            {
              key: 'agreements',
              label: tGlobal('entity.agreement', { context: 'plural' }),
              to: generatePath(PortfolioPaths.BuildingAgreements, { buildingId }),
              end: false,
            },
          ]
        : []),
      ...(!isPortfolioOnly && hasFinancialViewPermission
        ? [
            {
              key: 'agreementParties',
              label: tGlobal('entity.tenant', { context: 'plural' }),
              to: generatePath(PortfolioPaths.BuildingAgreementParties, { buildingId }),
              end: false,
            },
          ]
        : []),
      ...(hasFinancialViewPermission
        ? [
            {
              key: 'roles',
              label: tGlobal('entity.roles'),
              to: generatePath(PortfolioPaths.BuildingRoles, { buildingId }),
              end: false,
            },
          ]
        : []),
      ...(hasPortfolio && hasFinancialViewPermission
        ? [
            {
              key: 'portfolio',
              label: tGlobal('entity.economy'),
              to: generatePath(PortfolioPaths.BuildingPortfolio, { buildingId }),
              end: false,
            },
            {
              key: 'financing',
              label: tGlobal('entity.financing'),
              to: generatePath(PortfolioPaths.BuildingFinancing, { buildingId }),
              end: false,
            },
          ]
        : []),
    ];

    if (hasEos && hasEnergyPermission) {
      baseTabs.push({
        key: 'energy',
        label: tEos('energy'),
        to: generatePath(PortfolioPaths.BuildingEnergy, { buildingId }),
        end: false,
      });
    }

    if (hasUpdatePermission) {
      baseTabs.push({
        key: 'associations',
        label: tGlobal('space.associations'),
        to: generatePath(PortfolioPaths.BuildingAssociations, { buildingId }),
        end: false,
      });
    }

    return baseTabs;
  }, [
    tCommon,
    tGlobal,
    tEos,
    buildingId,
    hasEnergyPermission,
    hasUpdatePermission,
    hasFinancialViewPermission,
    hasManagement,
    hasPortfolio,
    hasEos,
    isPortfolioOnly,
  ]);

  const handleEditClick = () => {
    navigate(generatePath(PortfolioPaths.BuildingEdit, { buildingId }));
  };

  const handleDeleteClick = () => {
    setDeleting(true);
  };

  const handleCloseDelete = () => {
    setDeleting(false);
  };

  const handleConfirmClickDelete = () => {
    setDeleting(false);
  };

  return (
    <SpaceContext.Provider value={{ space: building, isFetching }}>
      <PageNavigation className="mb-2" />
      <PageDetailHeader align="left">
        <PageContentAnimation>
          <div className="mb-4">
            <div className="text-gray-400 font-medium text-sm">{tGlobal('entity.building')}</div>
            <Text
              className="text-xl font-medium"
              isEmpty={!isDefinedString(building?.name)}
              loading={isFetching}
              SkeletonProps={{ className: 'w-[35%]' }}
            >
              {building?.name}
            </Text>
            {isDefinedString(building?.buildingNumber) && (
              <div className="text-gray-400 text-sm mt-1">
                {tGlobal('building.buildingNumber')} {building?.buildingNumber}
              </div>
            )}
          </div>
          <div className="flex items-end">
            <div className="flex gap-10 border-b border-border flex-1">
              {tabs.map((tab, index) => (
                <NavLink
                  key={tab.key}
                  to={tab.to}
                  end={tab.end}
                  replace
                  className={({ isActive }) =>
                    classNames(
                      'pb-2 text-sm font-medium transition-colors relative whitespace-nowrap',
                      index === 0 ? 'ml-4' : '',
                      isActive
                        ? 'text-brand-primary after:absolute after:bottom-0 after:h-0.5 after:bg-brand-primary after:right-[-1.25rem] ' +
                            (index === 0 ? 'after:left-[-1rem]' : 'after:left-[-1.25rem]')
                        : 'text-white hover:text-brand-primary',
                    )
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </div>
            <div className="flex space-x-2 items-center ml-4">
              {building && (
                <SpacePermissionBasedCta
                  space={building}
                  loading={isFetching}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  editModal={<></>}
                  deleteModal={
                    <BuildingDeleteModal
                      building={building}
                      open={deleting}
                      onCancel={handleCloseDelete}
                      onDelete={handleConfirmClickDelete}
                    />
                  }
                />
              )}
            </div>
          </div>
        </PageContentAnimation>
      </PageDetailHeader>
      <Outlet />
    </SpaceContext.Provider>
  );
}
